import { IntroWidget } from "common/IntroWidget";
import { SegmentListItem } from "common/ListItems";
import { Loadable } from "common/Loadable";
import { SearchableCard } from "common/SearchableCard";
import cardStyles from "common/SearchableCard/SearchableCard.scss";
import { CTA } from "common/styleguide/CTA";
import { troutesMessages } from "features/homepage/shared/intlMessages";
import * as favoritesActions from "lib/api/favorites";
import * as segmentsActions from "lib/api/segments";
import { DEFAULT_PER_PAGE } from "lib/loadAllPages";
import { commonCTAs, commonMessages, Msg } from "lib/localization";
import _ from "lodash";
import React from "react";
import { FormattedMessage as T } from "react-intl";
import { connect } from "react-redux";
import styles from "./CreatedSegments.scss";

class _CreatedSegments extends React.PureComponent {
  state = {
    keywords: "",
    limit: DEFAULT_PER_PAGE,
  };

  favoriteForSegment = (segment) => this.props.favorites.get("favorites").find((f) => f.itemId === segment.id);

  loadMore = () => {
    this.setState((state) => ({ limit: state.limit + DEFAULT_PER_PAGE }));
  };

  render() {
    const { keywords, limit } = this.state;
    const { segments, dispatchPin, dispatchUnpin } = this.props;

    const createdSegments = segments.get("createdSegments").map((segmentId) => segments.getIn(["segments", segmentId]));
    const matches = keywords
      ? createdSegments.filter(({ title }) => title.toLowerCase().includes(keywords.toLowerCase()))
      : createdSegments;
    const toShow = matches.slice(0, limit);

    return (
      <div className={styles.CreatedSegments}>
        <SearchableCard
          keywords={keywords}
          onChange={(e) => this.setState({ keywords: e.target.value, limit: DEFAULT_PER_PAGE })}
          resultsCount={matches.count()}
          resultsLength={toShow.count()}
        >
          {toShow.map((segment) => (
            <SegmentListItem
              key={segment.slug}
              segment={segment}
              pin={dispatchPin}
              unpin={dispatchUnpin}
              pinnable
              pinned={this.favoriteForSegment(segment) && this.favoriteForSegment(segment).createdAt}
            />
          ))}
          {createdSegments.count() === 0 && !keywords && (
            <IntroWidget
              large
              i="md-podium"
              link={
                <a href="https://ridewithgps.com/help/segments#create" target="_blank">
                  <T id="MatchedSegments.createLearn" defaultMessage="Learn About Creating Segments" />
                </a>
              }
            >
              <T id="MatchedSegments.noCreated" defaultMessage="You have no created segments" />
            </IntroWidget>
          )}
          {toShow.count() === 0 && keywords.length > 0 && (
            <IntroWidget large i="md-podium">
              <Msg m={troutesMessages.noneMatched} />
            </IntroWidget>
          )}
          {matches.count() > toShow.count() && (
            <div className={cardStyles.resultsFooter}>
              <CTA inline secondary margins={false} onClick={() => this.loadMore()}>
                <Msg m={commonCTAs.loadMore} />
              </CTA>
              <div style={{ paddingTop: "20px" }}>
                <span className={cardStyles.footerText}>
                  <Msg
                    m={commonMessages.showingResults}
                    values={{
                      length: toShow.count(),
                      totalCount: matches.count(),
                    }}
                  />
                </span>
              </div>
            </div>
          )}
          {matches.count() === toShow.count() && toShow.count() > DEFAULT_PER_PAGE && (
            <div className={cardStyles.resultsFooter}>
              <span className={cardStyles.footerText}>
                <Msg m={commonMessages.noMoreItems} />
              </span>
            </div>
          )}
        </SearchableCard>
      </div>
    );
  }
}

const loadInitialResources = _.memoize(({ currentUser, loadPins, loadAllCreatedSegments }) => {
  const loadCurrentUserFavorites = () => loadPins(currentUser.id);

  const loadCurrentUserCreatedSegments = () => loadAllCreatedSegments(currentUser.id);

  const loadAll = Promise.all([loadCurrentUserFavorites(), loadCurrentUserCreatedSegments()]);

  return loadAll.then(([favorites, segments]) => ({
    favorites,
    segments,
  }));
});

const LoadableCreatedSegments = (props) => (
  <Loadable
    {...props}
    flashDelay={0}
    component={_CreatedSegments}
    loadingText={<T id="MatchedSegments.loadableMessage" defaultMessage="Loading your created segments" />}
    resourceLoaded={() => loadInitialResources(props)}
  />
);

const mapStateToProps = (state) => ({
  currentUser: state.currentUser,
  favorites: state.favorites,
  segments: state.segments,
});

const mapDispatchToProps = (dispatch, _) => ({
  loadPins: (userId) =>
    dispatch(
      favoritesActions.loadUserFavorites(userId, {
        object_types: JSON.stringify(["Segment"]),
      })
    ),
  loadAllCreatedSegments: (userId) => dispatch(segmentsActions.loadAllUserCreatedSegments(userId)),
  dispatchPin: (id) => dispatch(favoritesActions.pin("segment", id)),
  dispatchUnpin: (id) => dispatch(favoritesActions.unpin("segment", id)),
});

export const CreatedSegments = connect(mapStateToProps, mapDispatchToProps)(LoadableCreatedSegments);
